/* eslint-disable no-control-regex */
import {
  CHAT_GPT,
  DESCRIPTION_KEY,
  EPIC_ID_PREFIX,
  ID_KEY,
  SMALL_MODEL_PROMPT_WORD_THRESHOLD,
  STORY_ID_PREFIX,
  TASK_ID_PREFIX,
  TASK_SYSTEM_MESSAGES,
  SUMMARY_KEY
} from '../../common/Constants';
import {
  Epic,
  EpicCollection,
  EpicsResponse,
  EpicsWithStoriesResponse,
  Issue,
  IssueResponse,
  SingleEpicWithTasksResponse,
  SingleStoryWithTasksResponse,
  StoriesAndTasksResponse,
  EpicsWithTasksResponse,
  StoriesResponse,
  Story,
  StoryCollection
} from '../../common/Types';
import {
  formatChatGPTMessage,
  formatCollectionWithValidId,
  formatEpicsWithStories,
  formatEpicsWithTasks,
  formatSingleEpicWithTasks,
  formatSingleStoryWithTasksAsEpic,
  formatStoriesWithTasksAsEpics
} from '../../utils';
import { ChatGptMessage, ChatGptResponse, ChatGptRole } from './ChatGpt.types';
import { IStoryGeneratorApiClient } from './StoryGenerator.types';
import { FormData, ISSUE_TYPE } from '../../screen/GenerationPanel/Generationpanel.types';
import Strings from '../../common/Strings';
import { countWords } from '../../utils/aiUtils';
import { getModelConfig, getModelStatus, getRecommendedModel } from '../AIProviderService';
import { buildVolumeSystemPrompt, type VolumeLevel } from '../../config';

export default class StoryGeneratorApiService {
  storyGeneratorApiClient: IStoryGeneratorApiClient;

  constructor(storyGeneratorApiClient: IStoryGeneratorApiClient) {
    this.storyGeneratorApiClient = storyGeneratorApiClient;
  }

  private isSmallModel = (modelId?: string): boolean => {
    if (!modelId) return false;
    const modelConfig = getModelConfig(modelId);
    return Boolean(modelConfig?.isSmall);
  };

  private shouldBlockLargePrompt = (prompt: string, modelId?: string): boolean => {
    if (!prompt?.trim() || !modelId) return false;
    if (!this.isSmallModel(modelId)) return false;
    const wordCount = countWords(prompt);
    return wordCount >= SMALL_MODEL_PROMPT_WORD_THRESHOLD;
  };

  private isGatewayTimeoutError = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error ?? '');
    return /gateway timeout|\b504\b/i.test(message);
  };

  private normalizeGenerationError = (error: unknown): Error => {
    if (error instanceof Error && this.isGatewayTimeoutError(error)) {
      return error;
    }
    if (error instanceof Error && error.message === Strings.ERROR_MESSAGES.SMALL_MODEL_PROMPT_TOO_LARGE) {
      return error;
    }
    return new Error(Strings.ERROR_MESSAGES.AI_GENERATION_FAIL.BODY);
  };

  private getUpgradeModelId = (modelId?: string): string | undefined => {
    if (!modelId) return undefined;
    const modelConfig = getModelConfig(modelId);
    const upgradeId = modelConfig?.upgradeModelId;
    if (upgradeId && upgradeId !== modelId && getModelStatus(upgradeId) === 'enabled') {
      return upgradeId;
    }
    if (modelConfig?.provider) {
      const recommended = getRecommendedModel(modelConfig.provider);
      if (recommended?.id && recommended.id !== modelId && getModelStatus(recommended.id) === 'enabled') {
        return recommended.id;
      }
    }
    return undefined;
  };

  private shouldRetryGatewayTimeout = (
    error: unknown,
    modelId?: string,
    volume?: VolumeLevel
  ): boolean => {
    if (!this.isGatewayTimeoutError(error)) return false;
    if (volume !== 'high') return false;
    return this.isSmallModel(modelId);
  };

  private formatGeneratedString = (input: string): string => {
    let formattedString = input || '';

    const fencedMatch = formattedString.match(/```(?:json)?\s*([\s\S]*?)```/i);
    if (fencedMatch?.[1]) {
      formattedString = fencedMatch[1].trim();
    }

    formattedString = this.stripJsonPrefix(formattedString);

    const jsonMatch = formattedString.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
    if (jsonMatch?.[0]) {
      formattedString = jsonMatch[0];
    }

    formattedString = formattedString
      .replace(/[\u0000-\u001F\u0080-\u009F]/g, '')
      .replace(/\n/g, '\\n');
    return formattedString.replace(/`/gs, '');
  };

  private stripJsonPrefix = (value: string): string => {
    return (value || '').replace(/^\s*`{0,3}\s*json\s*[:-]?\s*/i, '').trimStart();
  };

  private parseGeneratedJson = (input: string) => {
    const sanitizedInput = this.stripJsonPrefix(input);
    const formatted = this.formatGeneratedString(sanitizedInput);

    const extractJsonSubstring = (value: string): string | null => {
      const sanitized = this.stripJsonPrefix(value);
      const objectStart = sanitized.indexOf('{');
      const objectEnd = sanitized.lastIndexOf('}');
      if (objectStart >= 0 && objectEnd > objectStart) {
        return sanitized.slice(objectStart, objectEnd + 1);
      }
      const arrayStart = sanitized.indexOf('[');
      const arrayEnd = sanitized.lastIndexOf(']');
      if (arrayStart >= 0 && arrayEnd > arrayStart) {
        return sanitized.slice(arrayStart, arrayEnd + 1);
      }
      return null;
    };

    const parseMaybeDouble = (value: string) => {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'string') {
        const stripped = this.stripJsonPrefix(parsed);
        const extracted = extractJsonSubstring(stripped) || stripped;
        try {
          return JSON.parse(extracted);
        } catch (err) {
          const unescaped = extracted
            .replace(/\\"/g, '"')
            .replace(/\\n/g, '\n')
            .replace(/\\\\/g, '\\');
          return JSON.parse(unescaped);
        }
      }
      return parsed;
    };

    try {
      return parseMaybeDouble(input);
    } catch (rawErr) {
      // fallthrough to formatted handling
    }

    try {
      return parseMaybeDouble(formatted);
    } catch (err) {
      // Some providers return stringified JSON with escaped quotes/newlines.
      // Attempt to unescape and parse again.
      const unescaped = formatted
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n')
        .replace(/\\\\/g, '\\');

      try {
        return parseMaybeDouble(unescaped);
      } catch (innerErr) {
        const extracted =
          extractJsonSubstring(input) ||
          extractJsonSubstring(unescaped) ||
          extractJsonSubstring(formatted);
        if (extracted) {
          return parseMaybeDouble(extracted);
        }
        throw err;
      }
    }
  };

  private coerceStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
      return [];
    }
    return value
      .filter(item => typeof item === 'string')
      .map(item => item.trim())
      .filter(Boolean);
  };

  private tryParseJsonObject = (value: string): Record<string, unknown> | null => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
      return null;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  };

  private deriveSummary = (
    description: string,
    acceptanceCriteria: string[],
    implementationSteps: string[]
  ): string => {
    const fallbackSource =
      description || acceptanceCriteria[0] || implementationSteps[0] || 'Task';
    const sentence = fallbackSource.split(/[.!?]/)[0]?.trim();
    const summary = sentence || fallbackSource.trim();
    return summary.length > 120 ? `${summary.slice(0, 117)}...` : summary;
  };

  private normalizeSingleTaskResponse = (task: unknown): Issue => {
    const record = (typeof task === 'object' && task !== null
      ? (task as Record<string, unknown>)
      : {}) as Record<string, unknown>;

    let summary = typeof record.summary === 'string' ? record.summary.trim() : '';
    let description = typeof record.description === 'string' ? record.description.trim() : '';
    let acceptanceCriteria = this.coerceStringArray(record.acceptanceCriteria);
    let implementationSteps = this.coerceStringArray(record.implementationSteps);

    const parsedFromDescription = description ? this.tryParseJsonObject(description) : null;
    if (parsedFromDescription) {
      const parsedDescription =
        typeof parsedFromDescription.description === 'string'
          ? parsedFromDescription.description.trim()
          : '';
      if (!description && parsedDescription) {
        description = parsedDescription;
      }
      acceptanceCriteria = acceptanceCriteria.length
        ? acceptanceCriteria
        : this.coerceStringArray(parsedFromDescription.acceptanceCriteria);
      implementationSteps = implementationSteps.length
        ? implementationSteps
        : this.coerceStringArray(parsedFromDescription.implementationSteps);
    }

    const hasCriteriaHeader = /acceptance criteria/i.test(description);
    const hasStepsHeader = /implementation steps/i.test(description);

    const descriptionSections: string[] = [];
    if (description) {
      descriptionSections.push(description);
    }
    if (acceptanceCriteria.length && !hasCriteriaHeader) {
      descriptionSections.push(`Acceptance Criteria:\n- ${acceptanceCriteria.join('\n- ')}`);
    }
    if (implementationSteps.length && !hasStepsHeader) {
      descriptionSections.push(`Implementation Steps:\n- ${implementationSteps.join('\n- ')}`);
    }

    const normalizedDescription = descriptionSections.join('\n\n').trim();
    if (!summary) {
      summary = this.deriveSummary(normalizedDescription, acceptanceCriteria, implementationSteps);
    }

    return {
      summary,
      description: normalizedDescription || description || ''
    };
  };

  private async generateUserStories(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<StoriesResponse> {
    try {
      const generatedUserStories = (await this.storyGeneratorApiClient.generateUserStories(
        userNotes,
        isChatGpt4,
        message,
        modelId
      )) as ChatGptResponse;
      const userStoriesString = generatedUserStories.choices[0]?.message.content || '{}';
      const userStories = this.parseGeneratedJson(userStoriesString);
      return { stories: userStories } as StoriesResponse;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateEpics(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<EpicsResponse> {
    try {
      const generatedEpics = (await this.storyGeneratorApiClient.generateEpics(
        userNotes,
        isChatGpt4,
        message,
        modelId
      )) as ChatGptResponse;
      const generatedEpicsString = generatedEpics.choices[0]?.message.content || '{}';
      const epics = this.parseGeneratedJson(generatedEpicsString);
      return { epics: epics } as EpicsResponse;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateSingleEpic(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<Issue> {
    try {
      const generatedEpic = (await this.storyGeneratorApiClient.generateSingleEpic(
        userNotes,
        isChatGpt4,
        message,
        modelId
      )) as ChatGptResponse;
      const generatedEpicsString = generatedEpic.choices[0]?.message.content || '{}';
      const epic = this.parseGeneratedJson(generatedEpicsString);
      return epic;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateSingleStory(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<Issue> {
    try {
      const generatedStory = (await this.storyGeneratorApiClient.generateSingleStory(
        userNotes,
        isChatGpt4,
        message,
        modelId
      )) as ChatGptResponse;
      const generatedStoryString = generatedStory.choices[0]?.message.content || '{}';
      const story = this.parseGeneratedJson(generatedStoryString);
      return story;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateEpicsAndUserStories(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<EpicsWithStoriesResponse> {
    try {
      const generatedEpicsAndUserStories =
        (await this.storyGeneratorApiClient.generateEpicsAndUserStories(
          userNotes,
          isChatGpt4,
          message,
          modelId
        )) as ChatGptResponse;
      const generatedEpicsAndUserStoriesString =
        generatedEpicsAndUserStories.choices[0]?.message.content || '{}';
      const epicsAndUserStories = this.parseGeneratedJson(
        generatedEpicsAndUserStoriesString
      );
      return epicsAndUserStories;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateSingleEpicAndUserStories(
    userNotes: string,
    message: ChatGptMessage[] = [],
    nextEpicIncrementKey: number,
    isChatGpt4 = false,
    modelId?: string
  ): Promise<{ epic: IssueResponse } & StoriesResponse> {
    try {
      const generatedEpicsAndUserStories =
        (await this.storyGeneratorApiClient.generateSingleEpicAndUserStories(
          userNotes,
          isChatGpt4,
          message,
          modelId
        )) as ChatGptResponse;
      const generatedEpicsAndUserStoriesString =
        generatedEpicsAndUserStories.choices[0]?.message.content || '{}';
      const epicsAndUserStories = this.parseGeneratedJson(
        generatedEpicsAndUserStoriesString
      );
      return epicsAndUserStories;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateTasks(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<StoriesResponse> {
    try {
      const generatedTasks = (await this.storyGeneratorApiClient.generateTasks(
        userNotes,
        isChatGpt4,
        message,
        modelId
      )) as ChatGptResponse;
      const tasksString = generatedTasks.choices[0]?.message.content || '{}';
      const tasks = this.parseGeneratedJson(tasksString);
      return { stories: tasks } as StoriesResponse;
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateSingleTask(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<Issue> {
    try {
      const generatedTask = (await this.storyGeneratorApiClient.generateSingleTask(
        userNotes,
        isChatGpt4,
        message,
        modelId
      )) as ChatGptResponse;
      const taskString = generatedTask.choices[0]?.message.content || '{}';
      const task = this.parseGeneratedJson(taskString);
      return this.normalizeSingleTaskResponse(task);
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateEpicsAndTasks(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<EpicsWithTasksResponse> {
    try {
      const generatedEpicsAndTasks =
        (await this.storyGeneratorApiClient.generateEpicsAndTasks(
          userNotes,
          isChatGpt4,
          message,
          modelId
        )) as ChatGptResponse;
      const generatedString = generatedEpicsAndTasks.choices[0]?.message.content || '{}';
      return this.parseGeneratedJson(generatedString);
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateSingleEpicAndTasks(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<SingleEpicWithTasksResponse> {
    try {
      const generatedEpicAndTasks =
        (await this.storyGeneratorApiClient.generateSingleEpicAndTasks(
          userNotes,
          isChatGpt4,
          message,
          modelId
        )) as ChatGptResponse;
      const generatedString = generatedEpicAndTasks.choices[0]?.message.content || '{}';
      return this.parseGeneratedJson(generatedString);
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateStoriesAndTasks(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<StoriesAndTasksResponse> {
    try {
      const generatedStoriesAndTasks =
        (await this.storyGeneratorApiClient.generateStoriesAndTasks(
          userNotes,
          isChatGpt4,
          message,
          modelId
        )) as ChatGptResponse;
      const generatedString = generatedStoriesAndTasks.choices[0]?.message.content || '{}';
      return this.parseGeneratedJson(generatedString);
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private async generateSingleStoryAndTasks(
    userNotes: string,
    message: ChatGptMessage[] = [],
    isChatGpt4 = false,
    modelId?: string
  ): Promise<SingleStoryWithTasksResponse> {
    try {
      const generatedStoryAndTasks =
        (await this.storyGeneratorApiClient.generateSingleStoryAndTasks(
          userNotes,
          isChatGpt4,
          message,
          modelId
        )) as ChatGptResponse;
      const generatedString = generatedStoryAndTasks.choices[0]?.message.content || '{}';
      return this.parseGeneratedJson(generatedString);
    } catch (err) {
      throw this.normalizeGenerationError(err);
    }
  }

  private buildVolumeSystemMessage = (
    issueType: ISSUE_TYPE,
    volume?: VolumeLevel
  ): ChatGptMessage | null => {
    if (!volume) return null;
    const prompt = buildVolumeSystemPrompt(issueType, volume);
    if (!prompt) return null;
    return {
      role: ChatGptRole.System,
      content: prompt
    };
  };

  /**
   * Generate epics, stories, or both based on the issue type
   * 
   * @param data - Form data with issue type and notes
   * @param isChatGpt4 - Legacy parameter (deprecated, use modelId instead)
   * @param nextStoryIncrementKey - Next story ID to use
   * @param nextEpicIncrementKey - Next epic ID to use
   * @param modelId - Optional: Model ID for multi-model support
   */
  async getGeneratedResult(
    data: FormData,
    isChatGpt4: boolean,
    nextStoryIncrementKey: number,
    nextEpicIncrementKey: number,
    modelId?: string,
    volume?: VolumeLevel,
    onRetryStart?: (fromModelId: string, toModelId: string) => void
  ) {
    const { issueType, notes } = data;
    if (this.shouldBlockLargePrompt(notes, modelId)) {
      throw new Error(Strings.ERROR_MESSAGES.SMALL_MODEL_PROMPT_TOO_LARGE);
    }
    const runGeneration = async (activeModelId?: string) => {
      const volumeMessage = this.buildVolumeSystemMessage(issueType.value as ISSUE_TYPE, volume);
      let epicCollection, storyCollection, epicWithStoryCollection;
      if (issueType.value === ISSUE_TYPE.MultipleEpics) {
        const message = formatChatGPTMessage(
          CHAT_GPT.SYSTEM_MESSAGES.FORMAT.BULK_EPICS,
          [nextEpicIncrementKey.toString()],
          ChatGptRole.System
        );
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        epicCollection = await this.generateEpics(notes, messages, isChatGpt4, activeModelId);
        epicCollection = {
          epics: formatCollectionWithValidId<Epic>(
            epicCollection.epics,
            EPIC_ID_PREFIX,
            nextEpicIncrementKey
          )
        };
      } else if (issueType.value === ISSUE_TYPE.MultipleStories) {
        const message = formatChatGPTMessage(
          CHAT_GPT.SYSTEM_MESSAGES.FORMAT.BULK_STORIES,
          [nextStoryIncrementKey.toString()],
          ChatGptRole.System
        );
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        storyCollection = await this.generateUserStories(notes, messages, isChatGpt4, activeModelId);
        storyCollection = {
          stories: formatCollectionWithValidId<Story>(
            storyCollection.stories,
            STORY_ID_PREFIX,
            nextStoryIncrementKey
          )
        };
      } else if (issueType.value === ISSUE_TYPE.EpicsAndStories) {
        const message = formatChatGPTMessage(
          CHAT_GPT.SYSTEM_MESSAGES.FORMAT.EPICS_AND_USER_STORIES,
          [nextStoryIncrementKey.toString(), nextEpicIncrementKey.toString()],
          ChatGptRole.System
        );
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        epicWithStoryCollection = await this.generateEpicsAndUserStories(
          notes,
          messages,
          isChatGpt4,
          activeModelId
        );
        epicWithStoryCollection = formatEpicsWithStories(
          epicWithStoryCollection,
          nextEpicIncrementKey,
          nextStoryIncrementKey
        );
      } else if (issueType.value === ISSUE_TYPE.SingleEpicMultiStories) {
        const message = formatChatGPTMessage(
          CHAT_GPT.SYSTEM_MESSAGES.FORMAT.SINGLE_EPIC_MULTIPLE_STORIES,
          [nextStoryIncrementKey.toString()],
          ChatGptRole.System
        );
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        const singleEpicWithStories = await this.generateSingleEpicAndUserStories(
          notes,
          messages,
          nextEpicIncrementKey,
          isChatGpt4,
          activeModelId
        );
        const epic = singleEpicWithStories.epic ?? {};
        const epicId = `${EPIC_ID_PREFIX}_${nextEpicIncrementKey}`;
        epicWithStoryCollection = {
          epics: { [epicId]: { [ID_KEY]: epicId, [SUMMARY_KEY]: epic.s, [DESCRIPTION_KEY]: epic.d } },
          stories: formatCollectionWithValidId<Story>(
            singleEpicWithStories.stories,
            STORY_ID_PREFIX,
            nextStoryIncrementKey,
            { epic_id: epicId }
          )
        };
      } else if (issueType.value === ISSUE_TYPE.SingleEpic) {
        const message = {
          role: ChatGptRole.System,
          content: CHAT_GPT.SYSTEM_MESSAGES.FORMAT.SINGLE_EPIC
        };
        const epic = await this.generateSingleEpic(notes, [message], isChatGpt4, activeModelId);
        const epicId = `${EPIC_ID_PREFIX}_${nextEpicIncrementKey}`;
        epicCollection = {
          epics: {
            [epicId]: {
              id: epicId,
              isNew: true,
              ...epic
            }
          }
        } as EpicCollection;
      } else if (issueType.value === ISSUE_TYPE.SingleStory) {
        const message = {
          role: ChatGptRole.System,
          content: CHAT_GPT.SYSTEM_MESSAGES.FORMAT.SINGLE_STORY
        };
        const story = await this.generateSingleStory(notes, [message], isChatGpt4, activeModelId);
        const storyId = `${STORY_ID_PREFIX}_${nextStoryIncrementKey}`;
        storyCollection = {
          stories: {
            [storyId]: {
              id: storyId,
              ...story
            }
          }
        } as StoryCollection;
      } else if (issueType.value === ISSUE_TYPE.MultipleTasks) {
        const message = formatChatGPTMessage(
          TASK_SYSTEM_MESSAGES.FORMAT.BULK_TASKS,
          [nextStoryIncrementKey.toString()],
          ChatGptRole.System
        );
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        storyCollection = await this.generateTasks(notes, messages, isChatGpt4, activeModelId);
        storyCollection = {
          stories: formatCollectionWithValidId<Story>(
            storyCollection.stories,
            TASK_ID_PREFIX,
            nextStoryIncrementKey
          )
        };
      } else if (issueType.value === ISSUE_TYPE.SingleTask) {
        const message = {
          role: ChatGptRole.System,
          content: TASK_SYSTEM_MESSAGES.FORMAT.SINGLE_TASK
        };
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        const task = await this.generateSingleTask(notes, messages, isChatGpt4, activeModelId);
        const taskId = `${TASK_ID_PREFIX}_${nextStoryIncrementKey}`;
        storyCollection = {
          stories: {
            [taskId]: {
              id: taskId,
              ...task
            }
          }
        } as StoryCollection;
      } else if (issueType.value === ISSUE_TYPE.EpicsAndTasks) {
        const message = {
          role: ChatGptRole.System,
          content: TASK_SYSTEM_MESSAGES.FORMAT.EPICS_AND_TASKS
        };
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        const epicsAndTasks = await this.generateEpicsAndTasks(
          notes,
          messages,
          isChatGpt4,
          activeModelId
        );
        epicWithStoryCollection = formatEpicsWithTasks(
          epicsAndTasks,
          nextEpicIncrementKey,
          nextStoryIncrementKey
        );
      } else if (issueType.value === ISSUE_TYPE.SingleEpicMultiTasks) {
        const message = {
          role: ChatGptRole.System,
          content: TASK_SYSTEM_MESSAGES.FORMAT.SINGLE_EPIC_MULTIPLE_TASKS
        };
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        const epicAndTasks = await this.generateSingleEpicAndTasks(
          notes,
          messages,
          isChatGpt4,
          activeModelId
        );
        epicWithStoryCollection = formatSingleEpicWithTasks(
          epicAndTasks,
          nextEpicIncrementKey,
          nextStoryIncrementKey
        );
      } else if (issueType.value === ISSUE_TYPE.StoriesAndTasks) {
        const message = {
          role: ChatGptRole.System,
          content: TASK_SYSTEM_MESSAGES.FORMAT.STORIES_AND_TASKS
        };
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        const storiesAndTasks = await this.generateStoriesAndTasks(
          notes,
          messages,
          isChatGpt4,
          activeModelId
        );
        epicWithStoryCollection = formatStoriesWithTasksAsEpics(
          storiesAndTasks,
          nextEpicIncrementKey,
          nextStoryIncrementKey
        );
      } else if (issueType.value === ISSUE_TYPE.SingleStoryMultiTasks) {
        const message = {
          role: ChatGptRole.System,
          content: TASK_SYSTEM_MESSAGES.FORMAT.SINGLE_STORY_MULTIPLE_TASKS
        };
        const messages = volumeMessage ? [message, volumeMessage] : [message];
        const storyAndTasks = await this.generateSingleStoryAndTasks(
          notes,
          messages,
          isChatGpt4,
          activeModelId
        );
        epicWithStoryCollection = formatSingleStoryWithTasksAsEpic(
          storyAndTasks,
          nextEpicIncrementKey,
          nextStoryIncrementKey
        );
      }
      return {
        epicCollection,
        storyCollection,
        epicWithStoryCollection,
        modelIdUsed: activeModelId,
        didRetry: false,
        retryFromModelId: undefined,
        retryToModelId: undefined
      };
    };

    try {
      return await runGeneration(modelId);
    } catch (error) {
      if (!this.shouldRetryGatewayTimeout(error, modelId, volume)) {
        throw error;
      }

      const upgradeModelId = this.getUpgradeModelId(modelId);
      if (!upgradeModelId) {
        throw error;
      }

      // Notify caller that retry is starting (before the retry call)
      if (onRetryStart && modelId) {
        onRetryStart(modelId, upgradeModelId);
      }

      const retryResult = await runGeneration(upgradeModelId);
      return {
        ...retryResult,
        modelIdUsed: upgradeModelId,
        didRetry: true,
        retryFromModelId: modelId,
        retryToModelId: upgradeModelId
      };
    }
  }
}
